import React, { useEffect, useState, useCallback } from "react";
import axios from "../../api";
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  Pagination,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Drawer,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  ListSubheader,
  Autocomplete,
} from "@mui/material";
import UndertakingDocuments from "../common/UndertakingDocuments";
import {
  Edit,
  AssignmentReturn,
  PersonAdd,
  Add,
  Devices,
  UploadFile,
  Build,
  Recycling,
  FactCheck,
  ForwardToInbox,
  MoreVert,
  Download,
  Inventory2,
  CheckCircle,
  GppMaybe,
  Payments,
  Close,
  Search as SearchIcon,
  Archive,
  ReportProblem,
  QrCode2,
  Print,
  PersonOff,
} from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminLayout from "../AdminLayout";
import BulkUploadDialog from "../common/BulkUploadDialog";
import StatCard from "../common/StatCard";
import { ASSET_CATEGORY_GROUPS } from "../../constants/assetCategories";

const ASSET_BULK_COLUMNS = [
  {
    key: "serialNumber",
    label: "serialNumber",
    required: true,
    note: "Unique serial number. Used to match existing assets (upsert). Required unless assetTag is provided.",
  },
  {
    key: "assetTag",
    label: "assetTag",
    note: "Asset tag / hostname. Alternate unique key if serialNumber is blank.",
  },
  { key: "model", label: "model", note: "e.g. ThinkPad X1 Carbon" },
  { key: "modelNo", label: "modelNo", note: "Manufacturer model number, e.g. 20XW" },
  { key: "manufacturer", label: "manufacturer", note: "e.g. Lenovo, Dell, Apple" },
  { key: "specifications", label: "specifications", note: "e.g. i7/16GB/512GB" },
  {
    key: "category",
    label: "category",
    note: "One of: Laptop, Desktop, Monitor, Phone, Tablet, Server, Networking, Biometric, CCTV, Accessory, Other (default Laptop)",
  },
  {
    key: "status",
    label: "status",
    note: "One of: InStock, Assigned, Returned, UnderRepair, Retired, Lost (default InStock)",
  },
  {
    key: "condition",
    label: "condition",
    note: "One of: New, Good, Fair, Poor, Damaged (default Good)",
  },
  { key: "site", label: "site", note: "Site / campus (optional)" },
  { key: "building", label: "building", note: "Building (optional)" },
  { key: "floor", label: "floor", note: "Floor / level (optional)" },
  { key: "location", label: "location", note: "Legacy free-text location (optional)" },
  { key: "vendor", label: "vendor", note: "Supplier the asset was purchased from" },
  { key: "poNumber", label: "poNumber", note: "Purchase order number" },
  { key: "invoiceNumber", label: "invoiceNumber", note: "Invoice number" },
  { key: "costCenter", label: "costCenter", note: "Accounting cost center" },
  { key: "purchaseCost", label: "purchaseCost", note: "Numeric purchase amount, e.g. 85000" },
  { key: "currency", label: "currency", note: "Currency code, e.g. INR, USD (default INR)" },
  { key: "purchaseDate", label: "purchaseDate", note: "YYYY-MM-DD" },
  { key: "warrantyExpiry", label: "warrantyExpiry", note: "YYYY-MM-DD" },
  { key: "notes", label: "notes", note: "Free text (optional)" },
  {
    key: "assignedEmail",
    label: "assignedEmail",
    note: "Optional. If set, the device is assigned to this employee on import (status becomes Assigned) so you can send verification links.",
  },
  { key: "assignedName", label: "assignedName", note: "Optional employee name for the assignment." },
  { key: "assignedEmpId", label: "assignedEmpId", note: "Optional employee ID for the assignment." },
];

const ASSET_BULK_SAMPLE = [
  {
    serialNumber: "SN-12345",
    assetTag: "LAP-001",
    model: "ThinkPad X1",
    modelNo: "20XW",
    manufacturer: "Lenovo",
    specifications: "i7/16GB/512GB",
    category: "Laptop",
    status: "InStock",
    condition: "New",
    site: "Bengaluru HQ",
    building: "Main",
    floor: "2",
    location: "Bengaluru HQ / Main / 2",
    vendor: "Lenovo India",
    poNumber: "PO-2024-001",
    invoiceNumber: "INV-5567",
    costCenter: "IT-CC-01",
    purchaseCost: 85000,
    currency: "INR",
    purchaseDate: "2024-01-15",
    warrantyExpiry: "2027-01-15",
    notes: "",
    assignedEmail: "",
    assignedName: "",
    assignedEmpId: "",
  },
  {
    serialNumber: "SN-SRV-01",
    assetTag: "SRV-BLR-01",
    model: "PowerEdge R750",
    modelNo: "R750",
    manufacturer: "Dell",
    specifications: "Rack server",
    category: "Server",
    status: "InStock",
    condition: "Good",
    site: "Bengaluru HQ",
    building: "Main",
    floor: "Basement",
    location: "Bengaluru HQ / Main / Basement",
    vendor: "Dell",
    poNumber: "",
    invoiceNumber: "",
    costCenter: "IT-INFRA",
    purchaseCost: "",
    currency: "INR",
    purchaseDate: "2024-06-01",
    warrantyExpiry: "2027-06-01",
    notes: "Rack A1 U20",
    assignedEmail: "",
    assignedName: "",
    assignedEmpId: "",
  },
  {
    serialNumber: "SN-SW-01",
    assetTag: "NET-BLR-CORE",
    model: "Catalyst 9300",
    modelNo: "",
    manufacturer: "Cisco",
    specifications: "48-port PoE",
    category: "Networking",
    status: "InStock",
    condition: "Good",
    site: "Bengaluru HQ",
    building: "Main",
    floor: "MDF",
    location: "",
    vendor: "Cisco Partner",
    poNumber: "",
    invoiceNumber: "",
    costCenter: "IT-INFRA",
    purchaseCost: "",
    currency: "INR",
    purchaseDate: "",
    warrantyExpiry: "",
    notes: "Core switch",
    assignedEmail: "",
    assignedName: "",
    assignedEmpId: "",
  },
  {
    serialNumber: "SN-BIO-01",
    assetTag: "BIO-GATE-1",
    model: "FaceStation 2",
    modelNo: "",
    manufacturer: "Suprema",
    specifications: "",
    category: "Biometric",
    status: "InStock",
    condition: "Good",
    site: "Bengaluru HQ",
    building: "Main",
    floor: "Ground",
    location: "",
    vendor: "",
    poNumber: "",
    invoiceNumber: "",
    costCenter: "FACILITIES",
    purchaseCost: "",
    currency: "INR",
    purchaseDate: "",
    warrantyExpiry: "",
    notes: "Main gate",
    assignedEmail: "",
    assignedName: "",
    assignedEmpId: "",
  },
  {
    serialNumber: "SN-CAM-01",
    assetTag: "CCTV-L1-01",
    model: "DS-2CD2143",
    modelNo: "",
    manufacturer: "Hikvision",
    specifications: "4MP dome",
    category: "CCTV",
    status: "InStock",
    condition: "Good",
    site: "Bengaluru HQ",
    building: "Main",
    floor: "1",
    location: "",
    vendor: "",
    poNumber: "",
    invoiceNumber: "",
    costCenter: "FACILITIES",
    purchaseCost: "",
    currency: "INR",
    purchaseDate: "",
    warrantyExpiry: "",
    notes: "Lobby camera",
    assignedEmail: "",
    assignedName: "",
    assignedEmpId: "",
  },
];

const STATUS_COLORS = {
  InStock: "default",
  Assigned: "primary",
  Returned: "info",
  UnderRepair: "warning",
  Retired: "secondary",
  Lost: "error",
};

const STATUSES = ["InStock", "Assigned", "Returned", "UnderRepair", "Retired", "Lost"];

const STATUS_LABELS = {
  InStock: "In Stock",
  Assigned: "Assigned",
  Returned: "Returned",
  UnderRepair: "Under Repair",
  Retired: "Retired",
  Lost: "Lost",
};

const STATUS_CARD = {
  InStock: { color: "info", icon: <Inventory2 /> },
  Assigned: { color: "success", icon: <CheckCircle /> },
  Returned: { color: "info", icon: <AssignmentReturn /> },
  UnderRepair: { color: "warning", icon: <Build /> },
  Retired: { color: "secondary", icon: <Archive /> },
  Lost: { color: "error", icon: <ReportProblem /> },
};
const CONDITIONS = ["New", "Good", "Fair", "Poor", "Damaged"];
const DEPRECIATION_METHODS = ["None", "StraightLine"];
const MAINTENANCE_TYPES = ["Repair", "Service", "Upgrade", "Inspection", "Other"];
const DISPOSAL_METHODS = ["Sold", "Scrapped", "Donated", "Recycled", "Lost", "Other"];

const WARRANTY_COLORS = {
  Active: "success",
  ExpiringSoon: "warning",
  Expired: "error",
  Unknown: "default",
};

const VERIFICATION_COLORS = {
  Verified: "success",
  Pending: "warning",
  Overdue: "error",
  NotRequired: "default",
  "Not sent": "default",
  "Not required": "default",
};

const verificationLabel = (a) => {
  const st = a?.verification?.status;
  if (!st || st === "NotRequired") return "Not required";
  return st;
};

const verificationLastAt = (a) => {
  const d = a?.verification?.lastVerifiedAt;
  return d ? new Date(d).toLocaleDateString() : "—";
};

const agentHealth = (a) => {
  if (!a?.agentLastSeenAt) return { key: "none", label: "No agent", color: "default" };
  const ageMs = Date.now() - new Date(a.agentLastSeenAt).getTime();
  const hours = ageMs / 3600000;
  if (hours <= 24) return { key: "online", label: "Online", color: "success" };
  if (hours <= 72) return { key: "stale", label: "Stale", color: "warning" };
  return { key: "offline", label: "Offline", color: "error" };
};

const agentCell = (a) => {
  if (!a?.agentLastSeenAt && !a?.agentVersion) return "—";
  const health = agentHealth(a);
  const seen = a.agentLastSeenAt
    ? new Date(a.agentLastSeenAt).toLocaleString()
    : "";
  const ver = a.agentVersion ? `v${a.agentVersion}` : "";
  const label = [health.label, ver].filter(Boolean).join(" · ");
  return (
    <Tooltip title={seen ? `Last seen ${seen}` : "Reported by device agent"}>
      <Chip size="small" color={health.color} variant="outlined" label={label} />
    </Tooltip>
  );
};

const CONDITION_COLORS = {
  New: "success",
  Good: "success",
  Fair: "warning",
  Poor: "warning",
  Damaged: "error",
};

// yyyy-mm-dd for <input type="date"> from any date-ish value
const dateInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");
const fmtMoney = (v, c) =>
  v || v === 0 ? `${c || ""} ${Number(v).toLocaleString()}`.trim() : "-";

const emptyAsset = {
  assetTag: "",
  serialNumber: "",
  model: "",
  modelNo: "",
  manufacturer: "",
  specifications: "",
  category: "Laptop",
  status: "InStock",
  condition: "Good",
  site: "",
  building: "",
  floor: "",
  location: "",
  vendor: "",
  poNumber: "",
  invoiceNumber: "",
  costCenter: "",
  purchaseCost: "",
  currency: "INR",
  purchaseDate: "",
  warrantyExpiry: "",
  depreciationMethod: "None",
  usefulLifeMonths: "",
  salvageValue: "",
  notes: "",
};

function Assets({ onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "");
  const [assigneeFilter, setAssigneeFilter] = useState(
    () => searchParams.get("assignee") || ""
  );
  const [agentFilter, setAgentFilter] = useState(
    () => searchParams.get("agent") || ""
  );
  const [categoryFilter, setCategoryFilter] = useState("");
  const [verificationFilter, setVerificationFilter] = useState(
    () => searchParams.get("verification") || ""
  );
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAsset, setMenuAsset] = useState(null);
  const [detailAsset, setDetailAsset] = useState(null);
  const [detailDocs, setDetailDocs] = useState(null);
  const [detailDocsLoading, setDetailDocsLoading] = useState(false);
  const [recycleSelected, setRecycleSelected] = useState([]);
  const [sendingVerification, setSendingVerification] = useState(false);

  const [selected, setSelected] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [view, setView] = useState(() =>
    searchParams.get("tab") === "recycle" ? "recycle" : "devices"
  ); // "devices" | "employees" | "recycle"
  const [locationCatalog, setLocationCatalog] = useState([]);
  const [fieldOptions, setFieldOptions] = useState({
    manufacturers: [],
    specifications: [],
  });
  const [recycleItems, setRecycleItems] = useState([]);
  const [recycleMeta, setRecycleMeta] = useState({ retentionDays: 30, expiredCount: 0 });
  const [siteFilter, setSiteFilter] = useState("");
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState("");
  const [empSearchDebounced, setEmpSearchDebounced] = useState("");
  const [empExporting, setEmpExporting] = useState(false);
  const [empPage, setEmpPage] = useState(1);
  const [empPerPage] = useState(10);
  const [empDialog, setEmpDialog] = useState(false);
  const [empDetail, setEmpDetail] = useState(null);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [returnDialog, setReturnDialog] = useState(false);
  const [current, setCurrent] = useState(emptyAsset);
  const [assignData, setAssignData] = useState({
    email: "",
    empName: "",
    empId: "",
    notify: true,
  });
  const [returnData, setReturnData] = useState({
    condition: "",
    notes: "",
    destination: "stock",
  });
  const [unassigningId, setUnassigningId] = useState(null);

  const [maintDialog, setMaintDialog] = useState(false);
  const [maintData, setMaintData] = useState({
    type: "Repair",
    date: dateInput(new Date()),
    cost: "",
    vendor: "",
    notes: "",
    setUnderRepair: false,
    resolve: false,
  });
  const [disposeDialog, setDisposeDialog] = useState(false);
  const [disposeData, setDisposeData] = useState({
    method: "Scrapped",
    date: dateInput(new Date()),
    value: "",
    notes: "",
  });
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [verifyData, setVerifyData] = useState({ note: "" });

  const fetchAssets = useCallback(async () => {
    try {
      const [listResp, statsResp] = await Promise.all([
        axios.get("/assets", {
          params: {
            page,
            perPage,
            search,
            status: statusFilter,
            category: categoryFilter,
            site: siteFilter,
            verification: verificationFilter,
            assignee: assigneeFilter || undefined,
          },
        }),
        axios.get("/assets/stats"),
      ]);
      setAssets(listResp.data.result || []);
      setTotal(listResp.data.pageData?.total || 0);
      setStats(statsResp.data.result || null);
    } catch (err) {
      console.error("Failed to fetch assets", err);
      toast.error("Could not load assets. Check your connection and try again.");
      setAssets([]);
      setTotal(0);
    }
  }, [page, perPage, search, statusFilter, categoryFilter, siteFilter, verificationFilter, assigneeFilter]);

  const visibleAssets = React.useMemo(() => {
    if (!agentFilter) return assets;
    return assets.filter((a) => agentHealth(a).key === agentFilter);
  }, [assets, agentFilter]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const sync = (key, val) => {
          if (val) next.set(key, val);
          else next.delete(key);
        };
        sync("status", statusFilter);
        sync("verification", verificationFilter);
        sync("search", search);
        sync("assignee", assigneeFilter);
        sync("agent", agentFilter);
        return next;
      },
      { replace: true }
    );
  }, [statusFilter, verificationFilter, search, assigneeFilter, agentFilter, setSearchParams]);

  const fetchRecycleBin = useCallback(async () => {
    try {
      const res = await axios.get("/assets/recycle-bin");
      setRecycleItems(res.data.result || []);
      setRecycleMeta({
        retentionDays: res.data.retentionDays || 30,
        expiredCount: res.data.expiredCount || 0,
      });
    } catch (err) {
      toast.error("Failed to load recycle bin");
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const resp = await axios.get("/assets/employees", {
        params: { search: empSearchDebounced },
      });
      setEmployees(resp.data.result || []);
    } catch (err) {
      console.error("Failed to fetch employees", err);
    }
  }, [empSearchDebounced]);

  useEffect(() => {
    const t = setTimeout(() => setEmpSearchDebounced(empSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [empSearch]);

  useEffect(() => {
    setEmpPage(1);
  }, [empSearchDebounced]);

  const fetchFieldOptions = useCallback(async () => {
    try {
      const res = await axios.get("/assets/field-options");
      setFieldOptions({
        manufacturers: res.data.result?.manufacturers || [],
        specifications: res.data.result?.specifications || [],
      });
    } catch {
      /* keep prior options */
    }
  }, []);

  useEffect(() => {
    axios
      .get("/settings")
      .then((res) => setLocationCatalog(res.data.result?.locations?.sites || []))
      .catch(() => {});
    fetchFieldOptions();
  }, [fetchFieldOptions]);

  useEffect(() => {
    if (view === "devices") fetchAssets();
    else if (view === "employees") fetchEmployees();
    else if (view === "recycle") fetchRecycleBin();
  }, [view, fetchAssets, fetchEmployees, fetchRecycleBin]);

  useEffect(() => {
    const deepId = searchParams.get("id");
    if (!deepId || detailAsset?._id === deepId) return undefined;
    let cancelled = false;
    axios
      .get(`/assets/${deepId}`)
      .then((res) => {
        if (!cancelled && res.data?.result) setDetailAsset(res.data.result);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [searchParams, detailAsset?._id]);

  useEffect(() => {
    if (!detailAsset?._id) {
      setDetailDocs(null);
      return undefined;
    }
    let cancelled = false;
    setDetailDocsLoading(true);
    axios
      .get(`/assets/${detailAsset._id}/documents`)
      .then((res) => {
        if (!cancelled) setDetailDocs(res.data.result || null);
      })
      .catch(() => {
        if (!cancelled) setDetailDocs(null);
      })
      .finally(() => {
        if (!cancelled) setDetailDocsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailAsset?._id]);

  const bulkSendVerification = async () => {
    if (!selected.length) return;
    if (
      !window.confirm(
        `Send verification links for ${selected.length} selected asset(s)? Already Verified are skipped unless you force re-send from the Verification page.`
      )
    )
      return;
    try {
      setSendingVerification(true);
      const res = await axios.post("/undertaking/verification/start", {
        assetIds: selected,
        includeVerified: false,
      });
      toast.success(res.data.message || "Verification sent.");
      if (res.data.skipped?.length) {
        toast.info(`Skipped ${res.data.skipped.length} already verified.`);
      }
      clearSelection();
      fetchAssets();
    } catch (err) {
      toast.error(
        err.response?.data?.error || err.response?.data?.message || "Send failed"
      );
    } finally {
      setSendingVerification(false);
    }
  };

  const openEmployee = async (email) => {
    try {
      const resp = await axios.get(`/assets/by-employee/${encodeURIComponent(email)}`);
      setEmpDetail(resp.data.result);
      setEmpDialog(true);
    } catch (err) {
      toast.error("Failed to load employee devices");
    }
  };

  const openCreate = () => {
    setCurrent(emptyAsset);
    fetchFieldOptions();
    setEditDialog(true);
  };
  const openEdit = (asset) => {
    setCurrent(asset);
    fetchFieldOptions();
    setEditDialog(true);
  };

  const saveAsset = async () => {
    try {
      // Coerce empty numeric/date fields to null so Mongoose doesn't choke on ""
      const numericKeys = ["purchaseCost", "usefulLifeMonths", "salvageValue"];
      const dateKeys = ["purchaseDate", "warrantyExpiry"];
      const payload = { ...current };
      numericKeys.forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
      });
      dateKeys.forEach((k) => {
        payload[k] = payload[k] ? payload[k] : null;
      });

      if (current._id) {
        await axios.put(`/assets/${current._id}`, payload);
        toast.success("Asset updated");
      } else {
        await axios.post("/assets", payload);
        toast.success("Asset created");
      }
      setEditDialog(false);
      fetchAssets();
      fetchFieldOptions();
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    }
  };

  const openMaintenance = (asset) => {
    setCurrent(asset);
    setMaintData({
      type: "Repair",
      date: dateInput(new Date()),
      cost: "",
      vendor: "",
      notes: "",
      setUnderRepair: asset.status !== "UnderRepair",
      resolve: false,
    });
    setMaintDialog(true);
  };

  const doMaintenance = async () => {
    try {
      await axios.post(`/assets/${current._id}/maintenance`, maintData);
      toast.success("Maintenance logged");
      setMaintDialog(false);
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to log maintenance");
    }
  };

  const openDispose = (asset) => {
    setCurrent(asset);
    setDisposeData({
      method: "Scrapped",
      date: dateInput(new Date()),
      value: "",
      notes: "",
    });
    setDisposeDialog(true);
  };

  const doDispose = async () => {
    try {
      await axios.post(`/assets/${current._id}/dispose`, disposeData);
      toast.success("Asset disposed / retired");
      setDisposeDialog(false);
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.error || "Dispose failed");
    }
  };

  const openVerify = (asset) => {
    setCurrent(asset);
    setVerifyData({ note: "" });
    setVerifyDialog(true);
  };

  const doVerify = async () => {
    try {
      await axios.post(`/assets/${current._id}/verify`, verifyData);
      toast.success("Verification recorded");
      setVerifyDialog(false);
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.error || "Verify failed");
    }
  };

  const doAssign = async () => {
    try {
      const resp = await axios.post(`/assets/${current._id}/assign`, assignData);
      toast.success(
        resp.data.notified
          ? "Asset assigned — instructions email sent"
          : "Asset assigned"
      );
      setAssignDialog(false);
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.error || "Assign failed");
    }
  };

  const sendInstructions = async (emails) => {
    try {
      const body = emails?.length ? { emails } : {};
      const resp = await axios.post("/users/send-instructions", body);
      toast.success(resp.data.message || "Instructions sent");
      if (resp.data.errors?.length) {
        toast.warning(`${resp.data.errors.length} failed to send`);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.response?.data?.error || "Failed to send instructions"
      );
    }
  };

  const doReturn = async () => {
    try {
      const endpoint =
        returnData.destination === "stock"
          ? `/assets/${current._id}/unassign`
          : `/assets/${current._id}/return`;
      const body = {
        condition: returnData.condition,
        notes: returnData.notes,
        destination: returnData.destination,
      };
      await axios.post(endpoint, body);
      toast.success(
        returnData.destination === "stock"
          ? "Device unassigned — back in stock"
          : returnData.destination === "retired"
            ? "Device unassigned and retired"
            : "Device returned"
      );
      setReturnDialog(false);
      fetchAssets();
      if (empDialog && empDetail?.email) {
        openEmployee(empDetail.email);
      }
      if (detailAsset?._id === current._id) {
        setDetailAsset(null);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Unassign failed"
      );
    }
  };

  /** Quick unassign to stock (used from employee dialog / row actions). */
  const unassignToStock = async (asset, { confirm = true } = {}) => {
    if (!asset?._id) return false;
    const label = asset.assetTag || asset.serialNumber || "this device";
    const who = asset.assignedTo?.empName || asset.assignedTo?.email || "the user";
    if (
      confirm &&
      !window.confirm(
        `Unassign "${label}" from ${who}?\n\nThe device will return to In Stock.`
      )
    ) {
      return false;
    }
    setUnassigningId(asset._id);
    try {
      await axios.post(`/assets/${asset._id}/unassign`, {
        notes: "Unassigned from user",
        destination: "stock",
      });
      toast.success(`Unassigned ${label}`);
      fetchAssets();
      if (empDialog && empDetail?.email) {
        await openEmployee(empDetail.email);
      }
      if (detailAsset?._id === asset._id) {
        setDetailAsset(null);
      }
      return true;
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Unassign failed"
      );
      return false;
    } finally {
      setUnassigningId(null);
    }
  };

  const unassignAllForEmployee = async () => {
    const list = empDetail?.current || [];
    if (!list.length) return;
    if (
      !window.confirm(
        `Unassign all ${list.length} device(s) from ${
          empDetail.empName || empDetail.email
        }?\n\nThey will return to In Stock.`
      )
    ) {
      return;
    }
    let ok = 0;
    for (const a of list) {
      setUnassigningId(a._id);
      try {
        await axios.post(`/assets/${a._id}/unassign`, {
          notes: `Unassigned from ${empDetail.email}`,
          destination: "stock",
        });
        ok += 1;
      } catch (err) {
        toast.error(
          err.response?.data?.message ||
            `Failed to unassign ${a.assetTag || a.serialNumber || a._id}`
        );
      }
    }
    setUnassigningId(null);
    if (ok) toast.success(`Unassigned ${ok} device(s)`);
    fetchAssets();
    if (empDetail?.email) openEmployee(empDetail.email);
  };

  const requestAck = async ({ assetId, email }) => {
    try {
      const resp = await axios.post("/undertaking/ack/request", { assetId, email });
      const sent = resp.data.sent?.length || 0;
      if (sent > 0) {
        toast.success(`Acknowledgment link sent (${sent})`);
      } else {
        toast.warning(resp.data.errors?.[0] || "No link sent");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || "Failed to send link");
    }
  };

  const deleteAsset = async (id) => {
    const choice = window.prompt(
      "Type BIN to move to recycle bin, or DELETE to permanently remove.\nLeave empty to cancel."
    );
    if (choice == null) return;
    const normalized = String(choice).trim().toUpperCase();
    if (!normalized) return;
    if (normalized === "BIN") {
      try {
        await axios.delete(`/assets/${id}`);
        toast.success("Moved to recycle bin");
        fetchAssets();
      } catch (err) {
        toast.error(err.response?.data?.error || "Move failed");
      }
      return;
    }
    if (normalized !== "DELETE") {
      toast.info("Cancelled — type BIN or DELETE to confirm.");
      return;
    }
    if (
      !window.confirm(
        "Permanently DELETE this asset? This cannot be undone."
      )
    )
      return;
    try {
      await axios.delete(`/assets/${id}`, { params: { permanent: true } });
      toast.success("Asset permanently deleted");
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  const openMenu = (e, asset) => {
    e.stopPropagation();
    setMenuAsset(asset);
    setMenuAnchor(e.currentTarget);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
  };
  // Run a row action against the asset the menu was opened for, then close.
  const runMenu = (fn) => () => {
    const a = menuAsset;
    closeMenu();
    fn(a);
  };

  const statCount = (id) =>
    (stats?.byStatus || []).find((s) => s._id === id)?.count || 0;

  const pageIds = assets.map((a) => a._id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const someSelected = selected.length > 0 && !allSelected;

  const toggleOne = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  const toggleAll = () =>
    setSelected((prev) =>
      allSelected ? prev.filter((id) => !pageIds.includes(id)) : [...new Set([...prev, ...pageIds])]
    );
  const clearSelection = () => setSelected([]);

  const applyBulkStatus = async () => {
    if (!bulkStatus || selected.length === 0) return;
    try {
      const resp = await axios.patch("/assets/bulk-status", {
        ids: selected,
        status: bulkStatus,
      });
      toast.success(`Updated ${resp.data.modified || 0} asset(s) to ${bulkStatus}`);
      setBulkStatus("");
      clearSelection();
      fetchAssets();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || "Bulk update failed");
    }
  };

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (
      !window.confirm(
        `Move ${selected.length} selected asset(s) to the recycle bin?\n\n` +
          "They can be restored from Recycle bin. Cancel aborts."
      )
    )
      return;
    try {
      setBulkDeleting(true);
      const resp = await axios.post("/assets/bulk-delete", { ids: selected });
      toast.success(`Moved ${resp.data.movedToBin || 0} asset(s) to recycle bin`);
      clearSelection();
      fetchAssets();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Bulk recycle failed"
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  const exportCsv = async () => {
    const cols = [
      "assetTag",
      "serialNumber",
      "model",
      "manufacturer",
      "category",
      "status",
      "condition",
      "location",
      "assignedEmail",
      "assignedName",
      "assignedEmpId",
      "verificationStatus",
      "lastVerifiedAt",
      "warrantyExpiry",
      "purchaseCost",
      "currentBookValue",
    ];

    // Pull every asset matching the CURRENT filters (not just the visible page).
    let rows = [];
    try {
      setExporting(true);
      const resp = await axios.get("/assets", {
        params: {
          page: 1,
          perPage: 100000,
          search,
          status: statusFilter,
          category: categoryFilter,
          site: siteFilter,
          verification: verificationFilter,
          assignee: assigneeFilter || undefined,
        },
      });
      rows = resp.data.result || [];
    } catch (err) {
      toast.error(err.response?.data?.error || "Export failed");
      setExporting(false);
      return;
    }
    setExporting(false);

    if (!rows.length) {
      toast.info("No assets to export for the current filters.");
      return;
    }

    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.join(",")];
    rows.forEach((a) => {
      lines.push(
        [
          a.assetTag,
          a.serialNumber,
          a.model,
          a.manufacturer,
          a.category,
          a.status,
          a.condition,
          a.location || [a.site, a.building, a.floor].filter(Boolean).join(" / "),
          ["Assigned", "UnderRepair"].includes(a.status) ? a.assignedTo?.email : "",
          ["Assigned", "UnderRepair"].includes(a.status) ? a.assignedTo?.empName : "",
          ["Assigned", "UnderRepair"].includes(a.status) ? a.assignedTo?.empId : "",
          verificationLabel(a),
          a.verification?.lastVerifiedAt
            ? new Date(a.verification.lastVerifiedAt).toISOString().slice(0, 10)
            : "",
          a.warrantyExpiry
            ? new Date(a.warrantyExpiry).toISOString().slice(0, 10)
            : "",
          a.purchaseCost,
          a.currentBookValue,
        ]
          .map(escape)
          .join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `assets-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} asset(s)`);
  };

  const downloadCsv = (filename, cols, rowArrays) => {
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.join(",")];
    rowArrays.forEach((row) => lines.push(row.map(escape).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportEmployeesCsv = async () => {
    // Re-fetch with current search so export matches the filtered list.
    let rows = employees;
    try {
      setEmpExporting(true);
      const resp = await axios.get("/assets/employees", {
        params: { search: empSearchDebounced },
      });
      rows = resp.data.result || [];
    } catch (err) {
      toast.error(err.response?.data?.error || "Export failed");
      setEmpExporting(false);
      return;
    }
    setEmpExporting(false);

    const deviceRows = [];
    rows.forEach((emp) => {
      (emp.devices || []).forEach((d) => {
        deviceRows.push([
          emp._id,
          emp.empName,
          emp.empId,
          emp.count,
          d.assetTag,
          d.serialNumber,
          d.model,
          d.category,
          d.status,
        ]);
      });
    });

    if (!deviceRows.length) {
      toast.info("No employee devices to export for the current search.");
      return;
    }

    downloadCsv(
      `assets-by-employee-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        "assignedEmail",
        "assignedName",
        "assignedEmpId",
        "deviceCount",
        "assetTag",
        "serialNumber",
        "model",
        "category",
        "status",
      ],
      deviceRows
    );
    toast.success(
      `Exported ${deviceRows.length} device(s) across ${rows.length} employee(s)`
    );
  };

  const exportEmployeeDetailCsv = () => {
    if (!empDetail) return;
    const current = empDetail.current || [];
    if (!current.length) {
      toast.info("No currently held devices to export for this employee.");
      return;
    }
    const rows = current.map((a) => [
      empDetail.email,
      empDetail.empName,
      empDetail.empId,
      a.assetTag,
      a.serialNumber,
      a.model,
      a.category,
      a.status,
      a.condition,
      a.location,
      a.assignedAt ? new Date(a.assignedAt).toISOString().slice(0, 10) : "",
    ]);
    const safe = (empDetail.empName || empDetail.email || "employee")
      .replace(/[^\w.-]+/g, "_")
      .slice(0, 40);
    downloadCsv(
      `employee-${safe}-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        "assignedEmail",
        "assignedName",
        "assignedEmpId",
        "assetTag",
        "serialNumber",
        "model",
        "category",
        "status",
        "condition",
        "location",
        "assignedAt",
      ],
      rows
    );
    toast.success(`Exported ${rows.length} device(s)`);
  };

  const handleLogout = () => {
    if (onLogout) return onLogout();
    localStorage.clear();
    navigate("/login");
  };

  const pageCount = Math.ceil(total / perPage) || 1;

  return (
    <AdminLayout title="Asset Inventory" onLogout={handleLogout}>
        <Container maxWidth="xl">
          <Paper elevation={3} sx={{ p: 3 }}>
            <Tabs
              value={view}
              onChange={(e, v) => setView(v)}
              sx={{ mb: 2 }}
            >
              <Tab value="devices" label="Devices" icon={<Devices />} iconPosition="start" />
              <Tab value="employees" label="By Employee" icon={<PersonAdd />} iconPosition="start" />
              <Tab
                value="recycle"
                icon={<Archive />}
                iconPosition="start"
                label={
                  recycleItems.length
                    ? `Recycle bin (${recycleItems.length})`
                    : "Recycle bin"
                }
              />
            </Tabs>

            {view === "devices" && (
            <>
            {/* KPI summary — CSS grid (MUI v7 Grid item/xs is unreliable here) */}
            <Box
              sx={{
                display: "grid",
                gap: 2,
                mb: 3,
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(5, minmax(0, 1fr))",
                  lg: "repeat(5, minmax(0, 1fr))",
                },
              }}
            >
              <StatCard
                label="Total"
                value={stats?.total ?? 0}
                icon={<Inventory2 />}
                color="primary"
                helper={statusFilter ? "Clear filter" : undefined}
                onClick={() => {
                  setPage(1);
                  setStatusFilter("");
                }}
              />
              {STATUSES.map((s) => (
                <StatCard
                  key={s}
                  label={STATUS_LABELS[s] || s}
                  value={statCount(s)}
                  icon={STATUS_CARD[s]?.icon}
                  color={STATUS_CARD[s]?.color || "neutral"}
                  helper={statusFilter === s ? "Filtered" : "Filter list"}
                  onClick={() => {
                    setPage(1);
                    setStatusFilter(statusFilter === s ? "" : s);
                  }}
                />
              ))}
              <StatCard
                label="Warranty ≤30d"
                value={stats?.warrantyExpiringSoon ?? 0}
                icon={<GppMaybe />}
                color="warning"
                helper="Open report"
                onClick={() => navigate("/warranty")}
              />
              <StatCard
                label="Purchase cost"
                value={(stats?.totalPurchaseCost || 0).toLocaleString()}
                icon={<Payments />}
                color="secondary"
              />
              <StatCard
                label="Book value"
                value={(stats?.totalBookValue || 0).toLocaleString()}
                helper="Straight-line est."
                icon={<Payments />}
                color="info"
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
              <TextField
                size="small"
                label="Assignee email"
                value={assigneeFilter}
                onChange={(e) => {
                  setPage(1);
                  setAssigneeFilter(e.target.value);
                }}
                sx={{ minWidth: 180 }}
                placeholder="Filter by holder"
              />
              <TextField
                size="small"
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value);
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All</MenuItem>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {STATUS_LABELS[s] || s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Category"
                value={categoryFilter}
                onChange={(e) => {
                  setPage(1);
                  setCategoryFilter(e.target.value);
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All</MenuItem>
                {ASSET_CATEGORY_GROUPS.map((g) => [
                  <ListSubheader key={`h-${g.label}`}>{g.label}</ListSubheader>,
                  ...g.items.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  )),
                ])}
              </TextField>
              <TextField
                size="small"
                select
                label="Site"
                value={siteFilter}
                onChange={(e) => {
                  setPage(1);
                  setSiteFilter(e.target.value);
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All</MenuItem>
                {locationCatalog.map((s) => (
                  <MenuItem key={s.name} value={s.name}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                select
                label="Verification"
                value={verificationFilter}
                onChange={(e) => {
                  setPage(1);
                  setVerificationFilter(e.target.value);
                }}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Verified">Verified</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Overdue">Overdue</MenuItem>
                <MenuItem value="DueSoon">Due ≤7 days</MenuItem>
                <MenuItem value="Not sent">Not required</MenuItem>
              </TextField>
              <TextField
                size="small"
                select
                label="Agent"
                value={agentFilter}
                onChange={(e) => {
                  setPage(1);
                  setAgentFilter(e.target.value);
                }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="online">Online (≤24h)</MenuItem>
                <MenuItem value="stale">Stale (≤72h)</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="none">No agent</MenuItem>
              </TextField>
              <Box sx={{ flexGrow: 1 }} />
              {selected.length > 0 && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<ForwardToInbox />}
                  disabled={sendingVerification}
                  onClick={bulkSendVerification}
                >
                  {sendingVerification
                    ? "Sending…"
                    : `Send verification (${selected.length})`}
                </Button>
              )}
              <Button
                variant="text"
                startIcon={<Download />}
                onClick={exportCsv}
                disabled={!total || exporting}
              >
                {exporting ? "Exporting…" : "Export"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFile />}
                onClick={() => setBulkOpen(true)}
              >
                Bulk Upload
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
                Add Asset
              </Button>
            </Box>

            {selected.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  flexWrap: "wrap",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selected.length} selected
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <TextField
                  size="small"
                  select
                  label="Set status to"
                  value={bulkStatus}
                  onChange={(e) => setBulkStatus(e.target.value)}
                  sx={{
                    minWidth: 180,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                  }}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {STATUS_LABELS[s] || s}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={!bulkStatus}
                  onClick={applyBulkStatus}
                >
                  Apply
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Archive />}
                  disabled={bulkDeleting}
                  onClick={bulkDelete}
                >
                  {bulkDeleting ? "Working…" : "Move to bin"}
                </Button>
                <Button
                  variant="text"
                  sx={{ color: "primary.contrastText" }}
                  onClick={clearSelection}
                >
                  Clear
                </Button>
              </Box>
            )}

            {isMobile ? (
              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {visibleAssets.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                      inputProps={{ "aria-label": "Select all assets on this page" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Select all on page
                      {selected.length > 0 ? ` · ${selected.length} selected` : ""}
                    </Typography>
                  </Box>
                )}
                {visibleAssets.map((a) => (
                  <Paper
                    key={a._id}
                    variant="outlined"
                    sx={{ p: 1.5, cursor: "pointer" }}
                    onClick={() => setDetailAsset(a)}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Checkbox
                        size="small"
                        checked={selected.includes(a._id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleOne(a._id)}
                        inputProps={{
                          "aria-label": `Select asset ${a.assetTag || a.serialNumber || a._id}`,
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {a.model || a.assetTag || "Device"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {[a.assetTag, a.serialNumber].filter(Boolean).join(" · ") || "—"}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                          <Chip size="small" label={STATUS_LABELS[a.status] || a.status} color={STATUS_COLORS[a.status] || "default"} />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={verificationLabel(a)}
                            color={VERIFICATION_COLORS[verificationLabel(a)] || "default"}
                          />
                          {a.category && (
                            <Chip size="small" variant="outlined" label={a.category} />
                          )}
                        </Box>
                        {["Assigned", "UnderRepair"].includes(a.status) &&
                          a.assignedTo?.empName && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
                              {a.assignedTo.empName}
                              {a.assignedTo.email ? ` · ${a.assignedTo.email}` : ""}
                            </Typography>
                          )}
                        <Typography variant="caption" color="text.secondary">
                          Last verified: {verificationLastAt(a)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMenu(e, a);
                        }}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
                {!assets.length && (
                  <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                    No assets found
                  </Typography>
                )}
              </Stack>
            ) : (
            <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={toggleAll}
                      inputProps={{ "aria-label": "Select all assets" }}
                    />
                  </TableCell>
                  <TableCell>Asset Tag</TableCell>
                  <TableCell>Serial</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Warranty</TableCell>
                  <TableCell>Verification</TableCell>
                  <TableCell>Last verified</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleAssets.map((a) => (
                  <TableRow
                    key={a._id}
                    hover
                    selected={selected.includes(a._id)}
                    onClick={() => setDetailAsset(a)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selected.includes(a._id)}
                        onChange={() => toggleOne(a._id)}
                        inputProps={{
                          "aria-label": `Select asset ${a.assetTag || a.serialNumber || a._id}`,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{a.assetTag || "-"}</TableCell>
                    <TableCell>{a.serialNumber || "-"}</TableCell>
                    <TableCell>
                      {a.model || "-"}
                      {a.manufacturer && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {a.manufacturer}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{a.category}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[a.status] || a.status}
                        color={STATUS_COLORS[a.status] || "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {a.condition ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={a.condition}
                          color={CONDITION_COLORS[a.condition] || "default"}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>{a.location || "-"}</TableCell>
                    <TableCell>
                      {a.warrantyStatus && a.warrantyStatus !== "Unknown" ? (
                        <Tooltip
                          title={
                            a.warrantyExpiry
                              ? `Expires ${new Date(a.warrantyExpiry).toLocaleDateString()}`
                              : ""
                          }
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            label={a.warrantyStatus}
                            color={WARRANTY_COLORS[a.warrantyStatus] || "default"}
                          />
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={verificationLabel(a)}
                        color={
                          VERIFICATION_COLORS[verificationLabel(a)] || "default"
                        }
                      />
                    </TableCell>
                    <TableCell>{verificationLastAt(a)}</TableCell>
                    <TableCell>{agentCell(a)}</TableCell>
                    <TableCell>
                      {["Assigned", "UnderRepair"].includes(a.status) && a.assignedTo?.email ? (
                        <Box sx={{ minWidth: 0 }}>
                          {a.assignedTo?.empName && (
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                              {a.assignedTo.empName}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
                            {a.assignedTo.email}
                          </Typography>
                          {a.assignedTo?.empId && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                              ID: {a.assignedTo.empId}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          flexWrap: "nowrap",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => openEdit(a)}
                          title="Edit"
                          aria-label={`Edit ${a.assetTag || a.serialNumber || "asset"}`}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        {["Assigned", "UnderRepair"].includes(a.status) && a.assignedTo?.email ? (
                          <>
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={unassigningId === a._id}
                              onClick={() => unassignToStock(a)}
                              title="Unassign from user"
                              aria-label={`Unassign ${a.assetTag || a.serialNumber || "asset"}`}
                            >
                              <PersonOff fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => requestAck({ assetId: a._id })}
                              title="Send undertaking link"
                              aria-label={`Send undertaking for ${a.assetTag || a.serialNumber || "asset"}`}
                            >
                              <ForwardToInbox fontSize="small" />
                            </IconButton>
                          </>
                        ) : a.status === "InStock" || a.status === "Returned" ? (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setCurrent(a);
                              setAssignData({ email: "", empName: "", empId: "", notify: true });
                              setAssignDialog(true);
                            }}
                            title="Assign"
                            aria-label={`Assign ${a.assetTag || a.serialNumber || "asset"}`}
                          >
                            <PersonAdd fontSize="small" />
                          </IconButton>
                        ) : null}
                        <IconButton
                          size="small"
                          onClick={(e) => openMenu(e, a)}
                          title="More"
                          aria-label={`More actions for ${a.assetTag || a.serialNumber || "asset"}`}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {assets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={14} align="center">
                      No assets found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(e, v) => setPage(v)}
              />
            </Box>
            </>
            )}

            {view === "employees" && (
            <>
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Search"
                placeholder="Name, email, emp ID, asset tag, serial…"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
                sx={{ minWidth: 280, flex: "1 1 280px", maxWidth: 420 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Typography variant="body2" color="textSecondary">
                {employees.length} employee(s) currently holding devices
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="text"
                startIcon={<Download />}
                onClick={exportEmployeesCsv}
                disabled={!employees.length || empExporting}
              >
                {empExporting ? "Exporting…" : "Export"}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ForwardToInbox />}
                disabled={!employees.length}
                onClick={() => {
                  if (
                    window.confirm(
                      `Send “what you need to do” instructions to all ${employees.length} employee(s) with assigned devices?`
                    )
                  ) {
                    sendInstructions(employees.map((e) => e._id));
                  }
                }}
              >
                Send instructions (all)
              </Button>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Emp ID</TableCell>
                  <TableCell align="center">Devices</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees
                  .slice((empPage - 1) * empPerPage, empPage * empPerPage)
                  .map((emp) => (
                  <TableRow key={emp._id}>
                    <TableCell>{emp._id}</TableCell>
                    <TableCell>{emp.empName || "-"}</TableCell>
                    <TableCell>{emp.empId || "-"}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        color={emp.count > 1 ? "primary" : "default"}
                        label={emp.count}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openEmployee(emp._id)}>
                        View devices
                      </Button>
                      <Button
                        size="small"
                        startIcon={<ForwardToInbox />}
                        onClick={() => sendInstructions([emp._id])}
                      >
                        Instructions
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {employees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {empSearchDebounced
                        ? "No employees match your search"
                        : "No employees with assigned devices"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {employees.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={Math.ceil(employees.length / empPerPage) || 1}
                  page={Math.min(
                    empPage,
                    Math.ceil(employees.length / empPerPage) || 1
                  )}
                  onChange={(e, v) => setEmpPage(v)}
                />
              </Box>
            )}
            </>
            )}

            {view === "recycle" && (
              <>
                <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <Typography variant="body2" color="text.secondary">
                    Restorable for {recycleMeta.retentionDays} days.
                    {recycleMeta.expiredCount
                      ? ` ${recycleMeta.expiredCount} past retention awaiting purge.`
                      : ""}
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  {recycleSelected.length > 0 && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={async () => {
                        try {
                          const res = await axios.post(
                            "/assets/recycle-bin/restore",
                            { ids: recycleSelected }
                          );
                          toast.success(
                            `Restored ${res.data.restored || 0} asset(s)`
                          );
                          setRecycleSelected([]);
                          fetchRecycleBin();
                          fetchAssets();
                        } catch (err) {
                          toast.error(
                            err.response?.data?.message || "Restore failed"
                          );
                        }
                      }}
                    >
                      Restore selected ({recycleSelected.length})
                    </Button>
                  )}
                  <Button size="small" variant="outlined" onClick={fetchRecycleBin}>
                    Refresh
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={async () => {
                      if (
                        !window.confirm(
                          "Permanently purge all expired recycle-bin items?"
                        )
                      )
                        return;
                      try {
                        const res = await axios.post("/assets/recycle-bin/purge");
                        toast.success(`Purged ${res.data.purged || 0} asset(s)`);
                        fetchRecycleBin();
                      } catch (err) {
                        toast.error(err.response?.data?.error || "Purge failed");
                      }
                    }}
                  >
                    Purge expired
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={
                            recycleItems.length > 0 &&
                            recycleItems.every((a) =>
                              recycleSelected.includes(a._id)
                            )
                          }
                          indeterminate={
                            recycleSelected.length > 0 &&
                            recycleSelected.length < recycleItems.length
                          }
                          onChange={() => {
                            if (
                              recycleSelected.length === recycleItems.length
                            ) {
                              setRecycleSelected([]);
                            } else {
                              setRecycleSelected(recycleItems.map((a) => a._id));
                            }
                          }}
                          inputProps={{ "aria-label": "Select all recycled assets" }}
                        />
                      </TableCell>
                      <TableCell>Asset</TableCell>
                      <TableCell>Serial</TableCell>
                      <TableCell>Prior status</TableCell>
                      <TableCell>Removed</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recycleItems.map((a) => (
                      <TableRow key={a._id} selected={recycleSelected.includes(a._id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={recycleSelected.includes(a._id)}
                            inputProps={{
                              "aria-label": `Select recycled asset ${a.assetTag || a.serialNumber || a._id}`,
                            }}
                            onChange={() =>
                              setRecycleSelected((prev) =>
                                prev.includes(a._id)
                                  ? prev.filter((x) => x !== a._id)
                                  : [...prev, a._id]
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {a.model || a.assetTag || "—"}
                          <Typography variant="caption" display="block" color="text.secondary">
                            {a.assetTag}
                          </Typography>
                        </TableCell>
                        <TableCell>{a.serialNumber || "—"}</TableCell>
                        <TableCell>{a.priorStatus || "—"}</TableCell>
                        <TableCell>
                          {a.deletedAt
                            ? new Date(a.deletedAt).toLocaleString()
                            : "—"}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={async () => {
                              try {
                                await axios.post(`/assets/${a._id}/restore`);
                                toast.success("Restored");
                                fetchRecycleBin();
                              } catch (err) {
                                toast.error(
                                  err.response?.data?.message || "Restore failed"
                                );
                              }
                            }}
                          >
                            Restore
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={async () => {
                              if (
                                !window.confirm(
                                  "Permanently delete this asset?"
                                )
                              )
                                return;
                              try {
                                await axios.delete(`/assets/${a._id}`, {
                                  params: { permanent: true },
                                });
                                toast.success("Deleted");
                                fetchRecycleBin();
                              } catch (err) {
                                toast.error("Delete failed");
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!recycleItems.length && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Recycle bin is empty
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </Paper>
        </Container>

      {/* Create / Edit dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{current._id ? "Edit Asset" : "Add Asset"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Identity
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            {[
              ["assetTag", "Asset Tag"],
              ["serialNumber", "Serial Number"],
              ["model", "Model"],
              ["modelNo", "Model No"],
            ].map(([key, label]) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  size="small"
                  label={label}
                  value={current[key] || ""}
                  onChange={(e) => setCurrent({ ...current, [key]: e.target.value })}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={fieldOptions.manufacturers}
                value={current.manufacturer || ""}
                onChange={(_, value) =>
                  setCurrent({ ...current, manufacturer: value || "" })
                }
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    setCurrent({ ...current, manufacturer: value || "" });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Manufacturer"
                    placeholder="Select or type"
                    helperText={
                      fieldOptions.manufacturers.length
                        ? "Choose from catalog / existing assets"
                        : "Type a manufacturer"
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={fieldOptions.specifications}
                value={current.specifications || ""}
                onChange={(_, value) =>
                  setCurrent({ ...current, specifications: value || "" })
                }
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    setCurrent({ ...current, specifications: value || "" });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Specifications"
                    placeholder="Select or type"
                    helperText={
                      fieldOptions.specifications.length
                        ? "Choose from catalog / existing assets"
                        : "Type specifications"
                    }
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" color="primary" gutterBottom>
            Classification & Location
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Category"
                value={current.category || "Laptop"}
                onChange={(e) => setCurrent({ ...current, category: e.target.value })}
              >
                {ASSET_CATEGORY_GROUPS.map((g) => [
                  <ListSubheader key={`form-h-${g.label}`}>{g.label}</ListSubheader>,
                  ...g.items.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  )),
                ])}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Status"
                value={current.status || "InStock"}
                onChange={(e) => setCurrent({ ...current, status: e.target.value })}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {STATUS_LABELS[s] || s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Condition"
                value={current.condition || "Good"}
                onChange={(e) => setCurrent({ ...current, condition: e.target.value })}
              >
                {CONDITIONS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Site"
                value={current.site || ""}
                onChange={(e) =>
                  setCurrent({
                    ...current,
                    site: e.target.value,
                    building: "",
                    floor: "",
                  })
                }
              >
                <MenuItem value="">—</MenuItem>
                {locationCatalog.map((s) => (
                  <MenuItem key={s.name} value={s.name}>
                    {s.name}
                  </MenuItem>
                ))}
                {current.site &&
                  !locationCatalog.some((s) => s.name === current.site) && (
                    <MenuItem value={current.site}>{current.site}</MenuItem>
                  )}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Building"
                value={current.building || ""}
                onChange={(e) =>
                  setCurrent({ ...current, building: e.target.value, floor: "" })
                }
              >
                <MenuItem value="">—</MenuItem>
                {(
                  locationCatalog.find((s) => s.name === current.site)?.buildings ||
                  []
                ).map((b) => (
                  <MenuItem key={b.name} value={b.name}>
                    {b.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Floor"
                value={current.floor || ""}
                onChange={(e) => setCurrent({ ...current, floor: e.target.value })}
              >
                <MenuItem value="">—</MenuItem>
                {(
                  (
                    locationCatalog.find((s) => s.name === current.site)?.buildings ||
                    []
                  ).find((b) => b.name === current.building)?.floors || []
                ).map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Location (override)"
                value={current.location || ""}
                onChange={(e) => setCurrent({ ...current, location: e.target.value })}
                helperText="Auto-filled from site/building/floor if left blank"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Cost Center"
                value={current.costCenter || ""}
                onChange={(e) => setCurrent({ ...current, costCenter: e.target.value })}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" color="primary" gutterBottom>
            Procurement & Warranty
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Vendor / Supplier"
                value={current.vendor || ""}
                onChange={(e) => setCurrent({ ...current, vendor: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="PO Number"
                value={current.poNumber || ""}
                onChange={(e) => setCurrent({ ...current, poNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Invoice Number"
                value={current.invoiceNumber || ""}
                onChange={(e) => setCurrent({ ...current, invoiceNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Purchase Cost"
                value={current.purchaseCost ?? ""}
                onChange={(e) => setCurrent({ ...current, purchaseCost: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Currency"
                value={current.currency || ""}
                onChange={(e) => setCurrent({ ...current, currency: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Purchase Date"
                InputLabelProps={{ shrink: true }}
                value={dateInput(current.purchaseDate)}
                onChange={(e) => setCurrent({ ...current, purchaseDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Warranty Expiry"
                InputLabelProps={{ shrink: true }}
                value={dateInput(current.warrantyExpiry)}
                onChange={(e) => setCurrent({ ...current, warrantyExpiry: e.target.value })}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" color="primary" gutterBottom>
            Depreciation
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                select
                label="Method"
                value={current.depreciationMethod || "None"}
                onChange={(e) =>
                  setCurrent({ ...current, depreciationMethod: e.target.value })
                }
              >
                {DEPRECIATION_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m === "StraightLine" ? "Straight-line" : m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Useful Life (months)"
                value={current.usefulLifeMonths ?? ""}
                onChange={(e) =>
                  setCurrent({ ...current, usefulLifeMonths: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Salvage Value"
                value={current.salvageValue ?? ""}
                onChange={(e) => setCurrent({ ...current, salvageValue: e.target.value })}
              />
            </Grid>
            {current._id && current.currentBookValue != null && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Current book value:{" "}
                  <strong>{fmtMoney(current.currentBookValue, current.currency)}</strong>
                </Typography>
              </Grid>
            )}
          </Grid>

          <Typography variant="subtitle2" color="primary" gutterBottom>
            Notes
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Notes"
            value={current.notes || ""}
            onChange={(e) => setCurrent({ ...current, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAsset}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Asset</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee Email"
                value={assignData.email}
                onChange={(e) => setAssignData({ ...assignData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee Name"
                value={assignData.empName}
                onChange={(e) => setAssignData({ ...assignData, empName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee ID"
                value={assignData.empId}
                onChange={(e) => setAssignData({ ...assignData, empId: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!assignData.notify}
                    onChange={(e) =>
                      setAssignData({ ...assignData, notify: e.target.checked })
                    }
                  />
                }
                label="Email instructions (what they need to do)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={doAssign}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unassign / Return dialog */}
      <Dialog open={returnDialog} onClose={() => setReturnDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Unassign device</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Remove{" "}
            <strong>
              {current?.assetTag || current?.serialNumber || "this device"}
            </strong>{" "}
            from{" "}
            <strong>
              {current?.assignedTo?.empName ||
                current?.assignedTo?.email ||
                "the current user"}
            </strong>
            .
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="After unassign"
                value={returnData.destination}
                onChange={(e) =>
                  setReturnData({ ...returnData, destination: e.target.value })
                }
              >
                <MenuItem value="stock">Back to In Stock (available)</MenuItem>
                <MenuItem value="returned">Mark as Returned</MenuItem>
                <MenuItem value="retired">Retire device</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Condition"
                value={returnData.condition}
                onChange={(e) => setReturnData({ ...returnData, condition: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes"
                value={returnData.notes}
                onChange={(e) => setReturnData({ ...returnData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialog(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={doReturn}>
            {returnData.destination === "stock"
              ? "Unassign to stock"
              : returnData.destination === "retired"
                ? "Unassign & retire"
                : "Unassign & mark returned"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance dialog */}
      <Dialog open={maintDialog} onClose={() => setMaintDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Maintenance — {current.assetTag || current.serialNumber || ""}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Type"
                value={maintData.type}
                onChange={(e) => setMaintData({ ...maintData, type: e.target.value })}
              >
                {MAINTENANCE_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={maintData.date}
                onChange={(e) => setMaintData({ ...maintData, date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Cost"
                value={maintData.cost}
                onChange={(e) => setMaintData({ ...maintData, cost: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Vendor"
                value={maintData.vendor}
                onChange={(e) => setMaintData({ ...maintData, vendor: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes"
                value={maintData.notes}
                onChange={(e) => setMaintData({ ...maintData, notes: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Asset status"
                value={
                  maintData.setUnderRepair ? "under" : maintData.resolve ? "resolve" : "keep"
                }
                onChange={(e) =>
                  setMaintData({
                    ...maintData,
                    setUnderRepair: e.target.value === "under",
                    resolve: e.target.value === "resolve",
                  })
                }
              >
                <MenuItem value="keep">Leave status unchanged</MenuItem>
                <MenuItem value="under">Mark Under Repair</MenuItem>
                <MenuItem value="resolve">Resolve (back to Assigned / In Stock)</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {(current.maintenanceHistory || []).length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Maintenance history
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Cost</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...current.maintenanceHistory].reverse().map((m, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        {m.date ? new Date(m.date).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell>{m.type || "-"}</TableCell>
                      <TableCell>{m.cost != null ? m.cost : "-"}</TableCell>
                      <TableCell>{m.vendor || "-"}</TableCell>
                      <TableCell>{m.notes || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={doMaintenance}>
            Log
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify dialog */}
      <Dialog open={verifyDialog} onClose={() => setVerifyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Physical Verification</DialogTitle>
        <DialogContent dividers>
          {current.lastAuditedAt && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Last verified: {new Date(current.lastAuditedAt).toLocaleDateString()}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Note (optional)"
            value={verifyData.note}
            onChange={(e) => setVerifyData({ ...verifyData, note: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={doVerify}>
            Mark Verified Today
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dispose dialog */}
      <Dialog open={disposeDialog} onClose={() => setDisposeDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Dispose / Retire Asset</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Method"
                value={disposeData.method}
                onChange={(e) => setDisposeData({ ...disposeData, method: e.target.value })}
              >
                {DISPOSAL_METHODS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                InputLabelProps={{ shrink: true }}
                value={disposeData.date}
                onChange={(e) => setDisposeData({ ...disposeData, date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Value recovered"
                value={disposeData.value}
                onChange={(e) => setDisposeData({ ...disposeData, value: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes"
                value={disposeData.notes}
                onChange={(e) => setDisposeData({ ...disposeData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisposeDialog(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={doDispose}>
            Confirm Disposal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee devices dialog */}
      <Dialog open={empDialog} onClose={() => setEmpDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
              Devices for {empDetail?.empName || empDetail?.email}
            </Typography>
            {empDetail?.empName && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {empDetail.email}
                {empDetail.empId ? ` · ID: ${empDetail.empId}` : ""}
              </Typography>
            )}
          </Box>
          {empDetail && (
            <Chip
              size="small"
              color="primary"
              label={`${empDetail.currentCount} currently held`}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          {empDetail?.currentCount > 0 && (
            <>
              <Button
                size="small"
                variant="text"
                startIcon={<Download />}
                onClick={exportEmployeeDetailCsv}
              >
                Export
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={<PersonOff />}
                disabled={Boolean(unassigningId)}
                onClick={unassignAllForEmployee}
              >
                Unassign all
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ForwardToInbox />}
                onClick={() => sendInstructions([empDetail.email])}
              >
                Send instructions
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ForwardToInbox />}
                onClick={() => requestAck({ email: empDetail.email })}
              >
                Send undertaking (all)
              </Button>
            </>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Currently assigned
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Asset Tag</TableCell>
                <TableCell>Serial</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Verification</TableCell>
                <TableCell>Last verified</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(empDetail?.current || []).map((a) => (
                <TableRow key={a._id}>
                  <TableCell>{a.assetTag || "-"}</TableCell>
                  <TableCell>{a.serialNumber || "-"}</TableCell>
                  <TableCell>{a.model || "-"}</TableCell>
                  <TableCell>{a.category}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[a.status] || a.status}
                      color={STATUS_COLORS[a.status] || "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={verificationLabel(a)}
                      color={VERIFICATION_COLORS[verificationLabel(a)] || "default"}
                    />
                  </TableCell>
                  <TableCell>{verificationLastAt(a)}</TableCell>
                  <TableCell>
                    {a.assignedAt ? new Date(a.assignedAt).toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => requestAck({ assetId: a._id })}
                      title="Send undertaking link"
                      aria-label={`Send undertaking for ${a.assetTag || a.serialNumber || "asset"}`}
                    >
                      <ForwardToInbox fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="warning"
                      disabled={unassigningId === a._id}
                      onClick={() => unassignToStock(a)}
                      title="Unassign from user"
                      aria-label={`Unassign ${a.assetTag || a.serialNumber || "asset"}`}
                    >
                      <PersonOff fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(!empDetail?.current || empDetail.current.length === 0) && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    None currently assigned
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Ever held (assignment history)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Asset Tag</TableCell>
                <TableCell>Serial</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Current Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(empDetail?.everHeld || []).map((a) => (
                <TableRow key={a._id}>
                  <TableCell>{a.assetTag || "-"}</TableCell>
                  <TableCell>{a.serialNumber || "-"}</TableCell>
                  <TableCell>{a.model || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[a.status] || a.status}
                      color={STATUS_COLORS[a.status] || "default"}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {(!empDetail?.everHeld || empDetail.everHeld.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No history
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmpDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Row action overflow menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={runMenu((a) => setDetailAsset(a))}>
          <ListItemIcon>
            <Devices fontSize="small" />
          </ListItemIcon>
          <ListItemText>View details</ListItemText>
        </MenuItem>
        <MenuItem onClick={runMenu(openEdit)}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {(menuAsset?.status === "InStock" ||
          menuAsset?.status === "Returned" ||
          !menuAsset?.assignedTo?.email) && (
          <MenuItem
            onClick={runMenu((a) => {
              setCurrent(a);
              setAssignData({ email: "", empName: "", empId: "", notify: true });
              setAssignDialog(true);
            })}
          >
            <ListItemIcon>
              <PersonAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign</ListItemText>
          </MenuItem>
        )}
        {["Assigned", "UnderRepair"].includes(menuAsset?.status) &&
          menuAsset?.assignedTo?.email && (
            <MenuItem
              onClick={runMenu((a) => {
                setCurrent(a);
                setReturnData({ condition: "", notes: "", destination: "stock" });
                setReturnDialog(true);
              })}
            >
              <ListItemIcon>
                <PersonOff fontSize="small" />
              </ListItemIcon>
              <ListItemText>Unassign / Return</ListItemText>
            </MenuItem>
          )}
        {["Assigned", "UnderRepair"].includes(menuAsset?.status) && menuAsset?.assignedTo?.email && (
          <MenuItem onClick={runMenu((a) => requestAck({ assetId: a._id }))}>
            <ListItemIcon>
              <ForwardToInbox fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send undertaking link</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={runMenu(openMaintenance)}>
          <ListItemIcon>
            <Build fontSize="small" />
          </ListItemIcon>
          <ListItemText>Log maintenance</ListItemText>
        </MenuItem>
        <MenuItem onClick={runMenu(openVerify)}>
          <ListItemIcon>
            <FactCheck fontSize="small" />
          </ListItemIcon>
          <ListItemText>Record verification</ListItemText>
        </MenuItem>
        <MenuItem onClick={runMenu(openDispose)}>
          <ListItemIcon>
            <Recycling fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dispose / retire</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={runMenu((a) => deleteAsset(a._id))}>
          <ListItemIcon>
            <Archive fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move to recycle bin…</ListItemText>
        </MenuItem>
      </Menu>

      {/* Asset detail side drawer */}
      <Drawer
        anchor="right"
        open={Boolean(detailAsset)}
        onClose={() => {
          setDetailAsset(null);
          if (searchParams.get("id")) {
            const next = new URLSearchParams(searchParams);
            next.delete("id");
            setSearchParams(next, { replace: true });
          }
        }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 440 },
            maxWidth: "100%",
          },
        }}
      >
        {detailAsset && (
          <Box
            sx={{
              p: 3,
              height: "100%",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: 1.5,
                position: "sticky",
                top: 0,
                bgcolor: "background.paper",
                zIndex: 1,
                pb: 1,
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0, pr: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {detailAsset.model || detailAsset.category || "Device"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ wordBreak: "break-all" }}
                >
                  {detailAsset.assetTag || detailAsset.serialNumber || "-"}
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setDetailAsset(null);
                  if (searchParams.get("id")) {
                    const next = new URLSearchParams(searchParams);
                    next.delete("id");
                    setSearchParams(next, { replace: true });
                  }
                }}
                size="small"
              >
                <Close />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Chip
                size="small"
                label={detailAsset.status}
                color={STATUS_COLORS[detailAsset.status] || "default"}
              />
              {detailAsset.condition && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={detailAsset.condition}
                  color={CONDITION_COLORS[detailAsset.condition] || "default"}
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`Verification: ${verificationLabel(detailAsset)}`}
                color={
                  VERIFICATION_COLORS[verificationLabel(detailAsset)] || "default"
                }
              />
              {detailAsset.warrantyStatus && detailAsset.warrantyStatus !== "Unknown" && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Warranty: ${detailAsset.warrantyStatus}`}
                  color={WARRANTY_COLORS[detailAsset.warrantyStatus] || "default"}
                />
              )}
            </Box>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  p: 1,
                  bgcolor: "#e8eaed",
                  borderRadius: 1,
                  border: "1px solid #0f172a",
                  lineHeight: 0,
                  minWidth: 128,
                }}
              >
                <QRCodeSVG
                  value={JSON.stringify({
                    id: detailAsset._id,
                    tag: detailAsset.assetTag || null,
                    serial: detailAsset.serialNumber || null,
                    url: `${window.location.origin}/assets?id=${detailAsset._id}`,
                  })}
                  size={112}
                  level="M"
                  includeMargin={false}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 160 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <QrCode2 fontSize="small" /> Asset label sticker
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Print-ready PDF with company brand, asset fields, barcodes, and QR.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Print />}
                    onClick={async () => {
                      try {
                        const r = await axios.get(
                          `/assets/${detailAsset._id}/label?format=pdf`,
                          { responseType: "blob" }
                        );
                        const url = URL.createObjectURL(r.data);
                        const w = window.open(url, "_blank");
                        if (!w) {
                          URL.revokeObjectURL(url);
                          toast.error("Allow pop-ups to print the label");
                          return;
                        }
                        setTimeout(() => {
                          try {
                            w.focus();
                            w.print();
                          } catch {
                            /* pdf viewer */
                          }
                        }, 700);
                        setTimeout(() => URL.revokeObjectURL(url), 60_000);
                      } catch {
                        toast.error("Failed to load asset label");
                      }
                    }}
                  >
                    Print label
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={async () => {
                      try {
                        const r = await axios.get(
                          `/assets/${detailAsset._id}/label?format=pdf`,
                          { responseType: "blob" }
                        );
                        const url = URL.createObjectURL(r.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `asset-${detailAsset.assetTag || detailAsset._id}-label.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch {
                        toast.error("Failed to download asset label PDF");
                      }
                    }}
                  >
                    PDF
                  </Button>
                </Stack>
              </Box>
            </Paper>

            {[
              {
                title: "Identity",
                rows: [
                  ["Asset Tag", detailAsset.assetTag],
                  ["Serial Number", detailAsset.serialNumber],
                  ["Model", detailAsset.model],
                  ["Model No", detailAsset.modelNo],
                  ["Manufacturer", detailAsset.manufacturer],
                  ["Category", detailAsset.category],
                  ["Specifications", detailAsset.specifications],
                  ["Location", detailAsset.location],
                  ...(detailAsset.agentVersion || detailAsset.agentLastSeenAt
                    ? [
                        ["Agent version", detailAsset.agentVersion],
                        [
                          "Agent last seen",
                          detailAsset.agentLastSeenAt
                            ? new Date(detailAsset.agentLastSeenAt).toLocaleString()
                            : null,
                        ],
                      ]
                    : []),
                ],
              },
              {
                title: "Assignment",
                rows: [
                  [
                    "Assigned To",
                    ["Assigned", "UnderRepair"].includes(detailAsset.status)
                      ? detailAsset.assignedTo?.email
                      : null,
                  ],
                  [
                    "Name",
                    ["Assigned", "UnderRepair"].includes(detailAsset.status)
                      ? detailAsset.assignedTo?.empName
                      : null,
                  ],
                  [
                    "Emp ID",
                    ["Assigned", "UnderRepair"].includes(detailAsset.status)
                      ? detailAsset.assignedTo?.empId
                      : null,
                  ],
                  [
                    "Assigned At",
                    ["Assigned", "UnderRepair"].includes(detailAsset.status) &&
                    detailAsset.assignedAt
                      ? new Date(detailAsset.assignedAt).toLocaleDateString()
                      : null,
                  ],
                ],
              },
              {
                title: "Verification",
                rows: [
                  ["Status", verificationLabel(detailAsset)],
                  [
                    "Last verified",
                    detailAsset.verification?.lastVerifiedAt
                      ? new Date(
                          detailAsset.verification.lastVerifiedAt
                        ).toLocaleString()
                      : null,
                  ],
                  [
                    "Last requested",
                    detailAsset.verification?.lastRequestedAt
                      ? new Date(
                          detailAsset.verification.lastRequestedAt
                        ).toLocaleString()
                      : null,
                  ],
                  [
                    "Due date",
                    detailAsset.verification?.dueDate
                      ? new Date(
                          detailAsset.verification.dueDate
                        ).toLocaleDateString()
                      : null,
                  ],
                  [
                    "Campaign",
                    detailAsset.verification?.campaignId || null,
                  ],
                ],
              },
              {
                title: "Financial",
                rows: [
                  ["Purchase Cost", fmtMoney(detailAsset.purchaseCost, detailAsset.currency)],
                  [
                    "Book Value",
                    detailAsset.currentBookValue != null
                      ? fmtMoney(detailAsset.currentBookValue, detailAsset.currency)
                      : null,
                  ],
                  [
                    "Purchase Date",
                    detailAsset.purchaseDate
                      ? new Date(detailAsset.purchaseDate).toLocaleDateString()
                      : null,
                  ],
                  [
                    "Warranty Expiry",
                    detailAsset.warrantyExpiry
                      ? new Date(detailAsset.warrantyExpiry).toLocaleDateString()
                      : null,
                  ],
                  ["Vendor", detailAsset.vendor],
                  ["PO Number", detailAsset.poNumber],
                  ["Invoice No", detailAsset.invoiceNumber],
                  ["Cost Center", detailAsset.costCenter],
                  [
                    "Depreciation",
                    detailAsset.depreciationMethod && detailAsset.depreciationMethod !== "None"
                      ? `${detailAsset.depreciationMethod} · ${detailAsset.usefulLifeMonths || "?"} mo`
                      : null,
                  ],
                ],
              },
              {
                title: "Notes & audit",
                rows: [
                  [
                    "Last Audited",
                    detailAsset.lastAuditedAt
                      ? new Date(detailAsset.lastAuditedAt).toLocaleDateString()
                      : null,
                  ],
                  ["Audit Note", detailAsset.lastAuditNote],
                  ["Notes", detailAsset.notes],
                ],
              },
            ].map((section) => {
              const visible = section.rows.filter(
                ([, v]) => v != null && v !== "" && v !== "-"
              );
              if (!visible.length) return null;
              return (
                <Box key={section.title} sx={{ mb: 2.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ fontWeight: 700, letterSpacing: 0.6 }}
                  >
                    {section.title}
                  </Typography>
                  <Divider sx={{ mb: 1.25 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {visible.map(([label, value]) => (
                      <Box
                        key={label}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "38% 1fr",
                          columnGap: 1.5,
                          alignItems: "start",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.45 }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.45,
                            wordBreak: "break-word",
                          }}
                        >
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}

            {(detailAsset.maintenanceHistory || []).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Maintenance History
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {detailAsset.maintenanceHistory.map((m, i) => (
                  <Box key={i} sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {m.type}
                      {m.date ? ` · ${new Date(m.date).toLocaleDateString()}` : ""}
                      {m.cost ? ` · ${fmtMoney(m.cost, detailAsset.currency)}` : ""}
                    </Typography>
                    {(m.vendor || m.notes) && (
                      <Typography variant="caption" color="text.secondary">
                        {[m.vendor, m.notes].filter(Boolean).join(" — ")}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ mb: 2.5 }}>
              {detailDocsLoading ? (
                <Typography variant="body2" color="text.secondary">
                  Loading uploaded documents…
                </Typography>
              ) : detailDocs ? (
                <UndertakingDocuments
                  idProofPath={detailDocs.idProofPath}
                  signaturePath={detailDocs.signaturePath}
                  assetImages={detailDocs.assetImages}
                  title="Uploaded documents"
                />
              ) : (
                <>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ fontWeight: 700 }}
                  >
                    Uploaded documents
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No ID proof, signature, or laptop photos linked to this asset
                    yet.
                  </Typography>
                </>
              )}
            </Box>

            {(detailAsset.assignmentHistory || []).length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Assignment History
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {[...detailAsset.assignmentHistory].reverse().map((h, i) => (
                  <Box key={i} sx={{ mb: 1.25 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {h.empName || h.email || "-"}
                      </Typography>
                      {h.empId && (
                        <Typography variant="caption" color="text.secondary">
                          ID: {h.empId}
                        </Typography>
                      )}
                      <Chip
                        size="small"
                        color={h.returnedAt ? "default" : "primary"}
                        label={h.returnedAt ? "Returned" : "Current"}
                        sx={{ height: 20 }}
                      />
                      {h.acknowledgedAt && (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label="Acknowledged"
                          sx={{ height: 20 }}
                        />
                      )}
                    </Box>
                    {h.email && h.empName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {h.email}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {h.assignedAt ? new Date(h.assignedAt).toLocaleDateString() : "?"}
                      {" → "}
                      {h.returnedAt
                        ? new Date(h.returnedAt).toLocaleDateString()
                        : "present"}
                      {h.condition ? ` · ${h.condition}` : ""}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => {
                  const a = detailAsset;
                  setDetailAsset(null);
                  openEdit(a);
                }}
              >
                Edit
              </Button>
              {["Assigned", "UnderRepair"].includes(detailAsset.status) &&
              detailAsset.assignedTo?.email ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<PersonOff />}
                    disabled={unassigningId === detailAsset._id}
                    onClick={() => {
                      setCurrent(detailAsset);
                      setReturnData({ condition: "", notes: "", destination: "stock" });
                      setReturnDialog(true);
                    }}
                  >
                    Unassign
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ForwardToInbox />}
                    onClick={() => requestAck({ assetId: detailAsset._id })}
                  >
                    Send undertaking
                  </Button>
                </>
              ) : detailAsset.status === "InStock" || detailAsset.status === "Returned" ? (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PersonAdd />}
                  onClick={() => {
                    const a = detailAsset;
                    setDetailAsset(null);
                    setCurrent(a);
                    setAssignData({ email: "", empName: "", empId: "", notify: true });
                    setAssignDialog(true);
                  }}
                >
                  Assign
                </Button>
              ) : null}
            </Box>
          </Box>
        )}
      </Drawer>

      <BulkUploadDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk Import Assets"
        endpoint="/assets/bulk"
        columns={ASSET_BULK_COLUMNS}
        sample={ASSET_BULK_SAMPLE}
        templateName="assets-template"
        onImported={() => fetchAssets()}
      />

      <ToastContainer position="top-center" autoClose={2500} />
    </AdminLayout>
  );
}

export default Assets;
